<template>
  <div>
    <v-dialog
        persistent
        v-model="showDeathReportDialog"
        max-width="1000"
        scrollable
        @keydown.esc="closeDialog()"
        overlay-opacity="0.9"
        :color="compColor.primeColor"
    >
      <div class="elevation-10 layout-column" style="border-radius: 6px;"
           v-bind:style="{background: compColor.contentFrame}">
        <ValidationObserver tag="div" ref="FormDetailForm" v-slot="{ pristine, valid, reset }" class="layout-column flex">

          <!-- HEADER -->

          <div class="text-h6 font-weight-light pa-3 layout-row layout-align-center-center"
               :class="isDarkMode? 'blue-grey--text text--lighten-2' : ' grey--text text--darken-2'">
            <div class="flex"></div>
            <span>{{ $t('FORMS_DIVERSE.MODULE_DEATH_DIALOG_HEADER') }}</span>
            <div class="flex"></div>
            <v-icon class="layout-align-end-end mr-1" @click="closeDialog()" :color="compColor.primeColor">cancel</v-icon>
          </div>

          <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

          <!-- CONTENT  -->

          <div class="ma-1 px-3 py-5 layout-column elevation-3" style="border-radius: 5px;overflow-y:auto;" :style="{'background': compColor.lxExpansion.background}">
            <lx-expansion-panels :single-open="false" v-model="panel">

              <!--   Melder  (panel 0)   -->

              <lx-expansion-panel :disabled="true" :isInvalid="openInvalidModule(0, 'report_header')">
                <lx-expansion-panel-header :disabled="true"
                                           icon="mdi-account-edit"
                                           :main-headline="$t('FORMS_DIVERSE.REPORTER_HEADER')"
                                           :sub-headline="$t('FORMS_DIVERSE.REPORTER_SUB_LINE')">
                </lx-expansion-panel-header>
                <lx-expansion-panel-content :delay="500">
                  <ValidationObserver tag="div" ref="report_header">
                    <div class="pa-4 layout-column">
                      <melder path="report_header"
                              :isDense="true"
                              form-key="death"
                              :isDisabled="isFormDisabled()"
                              :validationRef="$refs.FormDetailForm"
                              :isMedicalCommunity="isMedicalCommunity"
                              :additionalItemsList="additionalItemsList"
                              v-model="model.report_header"
                              class="layout-row layout-wrap flex-gt-sm-100 flex-100"></melder>
                    </div>
                  </ValidationObserver>
                </lx-expansion-panel-content>
              </lx-expansion-panel>

              <!--   Tod  (panel 1)   -->

              <lx-expansion-panel :disabled="true" :isInvalid="openInvalidModule(1, 'base')">
                <lx-expansion-panel-header :disabled="true"
                                           icon="mdi-shield-cross"
                                           :main-headline="$t('FORMS_DIVERSE.MODULE_DEATH_HEADER')"
                                           :sub-headline="$t('FORMS_DIVERSE.MODULE_DEATH_SUB_LINE')">
                </lx-expansion-panel-header>
                <lx-expansion-panel-content :delay="500">
                  <ValidationObserver tag="div" ref="base">
                    <div class="px-4 pt-4 layout-row layout-wrap">
                      <tod path="base"
                           :addSelectedItem="addItem"
                           :deleteSelectedItem="deleteItem"
                           :isDisabled="isFormDisabled()"
                           v-model="model"
                           :diagnoses="diagnoses"
                           :patientId="patientId"
                           :validationRef="$refs.FormDetailForm"
                           class="layout-wrap flex-gt-sm-100 flex-100"></tod>
                    </div>
                  </ValidationObserver>
                </lx-expansion-panel-content>
              </lx-expansion-panel>

              <!--   Anmerkung  (panel 3)   -->

              <lx-expansion-panel :isInvalid="openInvalidModule(2, 'anmerkung')">
                <lx-expansion-panel-header :main-headline="$t('FORMS_DIVERSE.ANNOTATION_HEADER')"
                                           :sub-headline="$t('FORMS_DIVERSE.ANNOTATION_SUB_LINE')"
                                           icon="mdi-lead-pencil">
                </lx-expansion-panel-header>
                <lx-expansion-panel-content :delay="500">
                  <ValidationObserver tag="div" ref="anmerkung">
                    <div class="pa-4 layout-column">
                      <anmerkung path="anmerkung"
                                 :isDense="true"
                                 v-model="model.anmerkung"
                                 :isDisabled="isFormDisabled()"
                                 class="layout-wrap flex-gt-sm-100 flex-100"></anmerkung>
                    </div>
                  </ValidationObserver>
                </lx-expansion-panel-content>
              </lx-expansion-panel>
            </lx-expansion-panels>
          </div>

          <!-- ACTIONS -->

          <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

          <div class="layout-row layout-align-end-center pt-1 pb-2 pr-1">
            <div class="layout-row">
              <!-- Clear form (Reset)  -->

              <lx-btn small classes="mr-1" :disabled="pristine && reasonsAreUnchanged()"
                      @click="clearForm(reset)"
                      icon="mdi-undo-variant" :tooltip="$t('buttons.revert.title')" :label="$t('buttons.revert.value')"
                      :hide-label="true"></lx-btn>

              <!-- Validate form and complete it if valid  -->

              <lx-btn small classes="mr-1" :disabled="!model._id || !!model.formStatus || !pristine"
                      icon-color="green"
                      @click="setIsCompleted(valid)"
                      icon="mdi-briefcase-check" :tooltip="$t('buttons.complete.title')" :label="$t('buttons.complete.value')"
                      :hide-label="true"></lx-btn>

              <!-- Ignore form for report  -->

              <lx-btn small classes="mr-1"
                      v-if="model._id && model.formStatus !== 'sent'"
                      :disabled="model.formStatus === 'ignoredForReport' || !pristine"
                      @click="setIgnoredForReport"
                      icon-color="grey"
                      icon="mdi-card-bulleted-off" :tooltip="$t('buttons.ignoreForImport.title')" :label="$t('buttons.ignoreForImport.value')"
                      :hide-label="true"></lx-btn>

              <!-- Reopen form to give possibility to edit  -->

              <lx-btn small classes="mr-1"
                      v-if="model._id && !!model.formStatus"
                      @click="reopenForm"
                      :icon-color="'#e8b20e'"
                      icon="mdi-clipboard-edit" :tooltip="$t('buttons.reopen.title')" :label="$t('buttons.reopen.value')"
                      :hide-label="true"></lx-btn>

              <v-divider vertical v-bind:style="{'border-color':compColor.lightDivider}" class="mx-4"></v-divider>

              <!-- Save form (Reset)  -->

              <lx-btn classes="mr-1" :disabled="!valid || (pristine && reasonsAreUnchanged())"
                      width="auto"
                      :iconColor="'white'"
                      :color="compColor.primeColor"
                      @click="saveFormData()"
                      icon="save" :tooltip="$t('buttons.save_form.tooltip')" :label="$t('buttons.save_form.value')"
                      :hide-label="false"></lx-btn>
            </div>
          </div>
        </ValidationObserver>
      </div>
    </v-dialog>

    <form-mismatch-dialog
        :title="formMismatchDialogTitle"
        :message="formMismatchDialogMessage"
        :showDialog="showFormMismatchDialog"
        :isLoading="isLoading"
        @close-form-update-dialog="showFormMismatchDialog=false"
    ></form-mismatch-dialog>
  </div>
</template>

<script>

import melder from '../../components/documentation/shared/Melder'
import tod from '../../components/documentation/shared/Tod'
import anmerkung from '../../components/documentation/shared/Anmerkung'
import FormMismatchDialog from '../../components/documentation/shared/FormMismatchDialog.vue'
import utils from '../../common/utils'
import lodash from 'lodash'

export default {
  name: "DeathReportDialog",
  mixins: [utils],
  components: {
    melder,
    tod,
    anmerkung,
    FormMismatchDialog
  },
  props: {
    showDeathReportDialog: Boolean,
    diagnoses: {
      type: Array,
      default: () => ([])
    },
    patientId: String,
    formId: String
  },
  data: () => {
    return {
      model: {
        key: 'death',
        base: {
          todesursachen: []
        }
      },
      oldModel: {
        key: 'death',
        base: {
          todesursachen: []
        }
      },
      isLoading: false,
      panel: [0, 1, 2],
      sideLocalizations: [
        { value: 'R', text: 'Rechts' },
        { value: 'L', text: 'Links' },
        { value: 'B', text: 'Beidseitig' },
        { value: 'M', text: 'Mittellinie/mittig' },
        { value: 'U', text: 'Unbekannt' },
        { value: 'T', text: 'Trifft nicht zu' }
      ],
      additionalItemsList: [],
      showFormMismatchDialog: false,
      formMismatchDialogTitle: '',
      formMismatchDialogMessage: '',
      setIsCompletedIsTriggered: false
    }
  },
  methods: {
    /**
     * Open dialog, if actually form is completed or deleted by other person
     *
     * @param errorMessage
     */
    openFormMismatchDialog (errorMessage) {
      if (this.showFormMismatchDialog) return

      this.formMismatchDialogTitle = 'FORM_MISMATCH_DIALOG.HEADER_' + errorMessage
      this.formMismatchDialogMessage = 'FORM_MISMATCH_DIALOG.MESSAGE_' + errorMessage
      this.showFormMismatchDialog = true

      // reload form or load new form (actually form is deleted)
      if (this.model?._id && errorMessage === 'FORM_ALREADY_COMPLETED') {
        this.getFormData(this.model._id)
      } else {
        this.getFormData()
      }
    },

    /**
     * Close dialog
     */
    closeDialog () {
      this.$emit('close-death-report-dialog')
    },

    /**
     * Reset validator, clear errorMsg and reset model
     */
    clearForm (reset) {
      reset()
      this.model = lodash.cloneDeep(this.oldModel)
      this.setIsCompletedIsTriggered = false
    },

    /**
     * Check if field todesursachen has changes to trigger clear function
     *
     * @returns {boolean}
     */
    reasonsAreUnchanged () {
      return lodash.isEqual(this.model.base.todesursachen, this.oldModel.base.todesursachen)
    },

    /**
     * Get form for validation
     *
     * @param id The id of already existing form
     */
    getFormData (id) {
      // reset flag to trigger that isCompleted is pressed when getting new data
      this.setIsCompletedIsTriggered = false

      if (id) {
        this.$http.get('/documentation/forms/' + id)
          .then(res => {
            let model = {}

            if (res.data) {
              model = res.data.form
              model.formStatus = res.data.formStatus
              model.report_header.zusatzitems = res.data.form.report_header.zusatzitems || []

              // set additional item list from cancer register
              this.additionalItemsList = res.data.additional_items || []
              this.setAdditionalItemsInDocumentation(model)

              this.model = model

              // reset field- and error-bag of validation to reset dirty-flags
              if (this.$refs.FormDetailForm) {
                this.$refs.FormDetailForm.reset()
              }

              // validate selected invalid form from report and delete stored information
              const storedPatient = this.$store.getters.routingAfterReportValidation(this.patientId)

              if (storedPatient && storedPatient.showFormData && storedPatient.formDetails.id) {
                // to set color on red to header of invalid modules set setIsCompletedIsTriggered on true
                this.setIsCompletedIsTriggered = true

                setTimeout(() => {
                  // validate invalid form to show validation errors from pre-validation in report dialog
                  this.setIsCompletedIsTriggered = true
                  this.$refs.FormDetailForm.validate()
                  this.errorMsg = this.$t('ERROR_VALIDATION_WHILE_COMPLETING_FORM')
                }, 100)

                // delete stored patient with validation errors in report
                this.$store.dispatch('deleteRoutingAfterReportValidation', this.patientId)
              }

              this.oldModel = lodash.cloneDeep(this.model)
              this.isLoading = false
            }
          })
          .catch(error => {
            this.isLoading = false
            this.NotifyError(error)
          })
      } else {
        this.$http.get('/documentation/newForm/death')
          .then(res => {
            if (res.data) {
              res.data.form = res.data.form || {}
              res.data.form.key = 'death'
              res.data.form.report_header.meldeanlass = 'tod'

              if (!res?.data?.base?.todesursachen) {
                res.data.form.base.todesursachen = []
              }

              // set diagnosis automatically, if only one exists, set v-select on disabled
              if (this.diagnoses.length === 1) {
                res.data.form.subj_id = this.diagnoses[0].subj_id
                res.data.form.register_form_id = this.diagnoses[0].diag_id
              }

              delete res.data.form._id

              res.data.form.patient_id = this.patientId
              res.data.form.report_header.zusatzitems = res.data.form.report_header.zusatzitems || []

              // set additional item list from cancer register
              this.additionalItemsList = res.data.additional_items || []
              this.setAdditionalItemsInDocumentation(res.data.form)

              this.model = res.data.form
              this.$set(this.model.report_header, 'eigenleistung', 'J')

              this.$delete(this.model.base, 'sterbedatum') // date will be set with default value in backend, because of its required

              // reset field- and error-bag of validation to reset dirty-flags
              if (this.$refs.FormDetailForm) {
                this.$refs.FormDetailForm.reset()
              }

              this.oldModel = lodash.cloneDeep(this.model)
            }
            // set timeout to delay opening watcher before validator was reset
            this.isLoading = false
          })
          .catch(error => {
            this.isLoading = false
            this.NotifyError(error)
          })
      }
    },

    /**
     * Save new or edited form in documentation
     */
    saveFormData () {
      if (this.isLoading) {
        return
      }

      this.isLoading = true

      if (this.model._id) {
        // update
        this.$http.put('/documentation/forms/' + this.model.key, this.model)
          .then(() => {
            this.oldModel = lodash.cloneDeep(this.model)

            // reset field- and error-bag of validation to reset dirty-flags
            if (this.$refs.FormDetailForm) {
              this.$refs.FormDetailForm.reset()
            }
            this.isLoading = false

            // trigger reloading of diagnosis list to actualize view of death form in list
            this.$eventBus.emit('reload-after-form-update-' + this.patientId)
          })
            .catch((error) => {
              this.serverErrorExists = true
              this.isLoading = false

              // if form is already completed or deleted by another person, popup will be opened and view will be refreshed
              if (error?.response?.status === 400 && error?.response?.data?.message && (error.response.data.message === 'FORM_ALREADY_COMPLETED' || error.response.data.message === 'FORM_NOT_FOUND')) {
                this.openFormMismatchDialog(error.response.data.message)
              } else {
                this.errorMsg = this.$t('ERROR_VALIDATION_WHILE_COMPLETING_FORM')
                this.populateFormError(this.$refs.FormDetailForm, error)
                    .catch(this.NotifyError)
              }
            })
      } else {
        // create
        this.$http.put('/documentation/forms/' + this.model.key, this.model)
          .then(res => {
            if (res?.data?.form && res.data.form._id) {
              this.model = res.data.form
              this.oldModel = lodash.cloneDeep(this.model)

              // reset field- and error-bag of validation to reset dirty-flags
              if (this.$refs.FormDetailForm) {
                this.$refs.FormDetailForm.reset()
              }
              this.isLoading = false

              // trigger reloading of diagnosis list to actualize view of death form in list
              this.$eventBus.emit('reload-after-form-update-' + this.patientId)
            }
          })
          .catch((error) => {
            this.populateFormError(this.$refs.FormDetailForm, error)
              .catch(this.NotifyError)
              .finally(() => {
                this.errorMsg = this.$t('ERROR_VALIDATION_WHILE_COMPLETING_FORM')
                this.isLoading = false
              })
          })
      }
    },

    /**
     * Set selected form in documentation on completed
     */
    setIsCompleted (isValid) {
      if (this.model._id) {
        this.errorMsg = null

        if (isValid) {
          this.$http.patch('/documentation/formStatus/completed/' + this.model._id)
            .then(() => {
              this.$nextTick(() => {
                this.$set(this.model, 'formStatus', 'completed')
              })
              // trigger reloading of patient, when last_modified on patient is older than actually date
              this.$eventBus.emit('reload-after-form-update-' + this.patientId)
            })
            .catch(this.NotifyError)
        } else {
          // validate and show validation errors on fields
          this.$refs.FormDetailForm.validate()
          this.setIsCompletedIsTriggered = true
        }
      }
    },

    /**
     * Set form on ignored for report
     */
    setIgnoredForReport () {
      if (this.model._id && this.model.formStatus !== 'sent') {
        this.$http.patch('/documentation/formStatus/ignoredForReport/' + this.model._id)
          .then(() => {
            this.$nextTick(() => {
              this.$set(this.model, 'formStatus', 'ignoredForReport')
              // trigger reloading of patient, when last_modified on patient is older than actually date
              this.$eventBus.emit('reload-after-form-update-' + this.patientId)
            })
          })
          .catch(this.NotifyError)
      }
    },

    /**
     * Reopen form to give possibility to edit
     */
    reopenForm () {
      if (this.model._id) {
        this.$http.patch('/documentation/formStatus/open/' + this.model._id)
            .then(() => {
              // set state of death form manually in frontend
              this.model.formStatus = null
            })
            .catch(this.NotifyError)
      }
    },

    /**
     * Disable or enable all form fields in dependency to existing form status
     *
     * @returns {boolean}
     */
    isFormDisabled () {
      return Boolean(this.model && this.model.formStatus)
    }
  },
  computed: {
    isMedicalCommunity () {
      const user = this.authStore.getters.getAuthUserData && this.authStore.getters.getAuthUserData.user ? this.authStore.getters.getAuthUserData.user : {}
      return user.cancer_form_details && user.cancer_form_details.isMedicalCommunity
    }
  },
  watch: {
    showDeathReportDialog: {
      handler: function (newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.isLoading = true

          this.model = {
            key: 'death',
            base: {
              todesursachen: []
            }
          }

          this.oldModel = lodash.cloneDeep(this.model)

          this.getFormData(this.formId)
        }
      },
      immediate: true
    }
  }
}
</script>
